import type { PlayerState } from './types'

export interface PersistedQueue {
  tracks: { id: number; radioGenerated: boolean }[]
  current: number
  position: number
}

/**
 * Pure function: shape the live queue into the play-queue save payload, or null
 * when there's nothing worth saving.
 *
 * Interstitials are dropped — their ids come from a different table, so the server
 * rejects the whole request as invalid track ids, and their audio is a one-off
 * synth stream that couldn't be replayed anyway. `radioGenerated` has to ride
 * along or a restored radio queue comes back looking manual, which makes radio
 * mode skip every track in it.
 */
export function toPersistedQueue(state: PlayerState, currentTime: number): PersistedQueue | null {
  const { queue, currentIndex } = state
  const tracks = queue
    .filter((item) => !item.isInterstitial)
    .map((item) => ({ id: item.id, radioGenerated: item.radioGenerated ?? false }))

  if (tracks.length === 0) return null

  // The playhead can sit on an interstitial, which isn't persisted — anchor the
  // restore point to the nearest real track behind it instead of dropping the save.
  const playing = queue[currentIndex]
  let current: number | null = null
  for (let i = Math.min(currentIndex, queue.length - 1); i >= 0; i--) {
    const item = queue[i]
    if (item && !item.isInterstitial) {
      current = item.id
      break
    }
  }
  if (current === null) return null

  return {
    tracks,
    current,
    // A saved position only means anything if the playhead is on a saved track.
    position: playing?.isInterstitial ? 0 : Math.floor(currentTime),
  }
}
